import { useCallback, useEffect, useState } from 'react';
import api from '@/services/api';

const isSupported = () =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)));
};

/**
 * Gere a permissão e subscrição de notificações push.
 * status: 'unsupported' | 'prompt' | 'granted' | 'denied'
 * subscribed: existe subscrição ativa registada no backend.
 */
export default function usePushNotifications() {
  const [status, setStatus] = useState(() =>
    isSupported() ? Notification.permission === 'default' ? 'prompt' : Notification.permission : 'unsupported');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isSupported()) return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setSubscribed(Boolean(sub)))
      .catch(() => {});
  }, []);

  const subscribe = useCallback(async () => {
    if (!isSupported() || loading) return false;
    setLoading(true);
    try {
      const permission = await Notification.requestPermission();
      setStatus(permission === 'default' ? 'prompt' : permission);
      if (permission !== 'granted') return false;

      const keyRes = await api.get('/api/player/push/public-key');
      const { publicKey, enabled } = keyRes.data.data || {};
      if (!enabled || !publicKey) return false;

      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }

      await api.post('/api/player/push/subscribe', { subscription: subscription.toJSON() });
      setSubscribed(true);
      return true;
    } catch {
      return false;
    } finally {
      setLoading(false);
    }
  }, [loading]);

  const unsubscribe = useCallback(async () => {
    if (!isSupported() || loading) return;
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await api.post('/api/player/push/unsubscribe', { endpoint: subscription.endpoint }).catch(() => {});
        await subscription.unsubscribe();
      }
      setSubscribed(false);
    } catch {}
    finally {
      setLoading(false);
    }
  }, [loading]);

  return { status, subscribed, loading, subscribe, unsubscribe, supported: isSupported() };
}
